import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@emotion/react';
import TemplateRenderer from './templates/TemplateRenderer';
import { buildTheme, deriveColorsFromAccent } from '../theme';

// Sample shipment so the preview renders standalone (no auth, no real AWB).
// Field names mirror the REAL order object (Order.js / OrderDetail.js / OrderTimeLine.js)
// so every template previews exactly what the live page will show.
const SAMPLE_ORDER = {
    currentStatus: 'OUT_FOR_DELIVERY',
    // OrderDetail.js reads these:
    shippingLabelId: 'PREVIEW-12345',
    waybill: '1234567890',
    paymentMode: 'PREPAID',
    currentLocation: 'Mumbai',
    courierPartnerParent: 'Delhivery',
    invoiceValue: 1499,
    pickupLocation: 'Delhi',
    deliveryLocation: 'Mumbai',
    createdDate: '2026-06-20',
    edd: '2026-06-24',
    // Kept for backward-compat with simpler template fields:
    orderId: 'PREVIEW-12345',
    awbNumber: '1234567890',
    // Shape matches what OrderTimeLine reads: orderStatusDescription / currentLocation / remark / timestamp.
    order_history: [
        { orderStatusDescription: 'Order Placed', currentLocation: 'Delhi', remark: 'Shipment information received', timestamp: '2026-06-20T10:00:00Z' },
        { orderStatusDescription: 'Picked Up', currentLocation: 'Delhi Hub', remark: 'Shipment picked up', timestamp: '2026-06-21T18:30:00Z' },
        { orderStatusDescription: 'In Transit', currentLocation: 'Jaipur Hub', remark: 'Shipment in transit', timestamp: '2026-06-22T09:00:00Z' },
        { orderStatusDescription: 'Out for Delivery', currentLocation: 'Mumbai', remark: 'Arriving today', timestamp: '2026-06-24T08:00:00Z' },
    ],
};

// Preview mode: render a chosen template with branding from query params + sample data.
// e.g. /preview?template=SPLIT&brandName=Acme&logo=https://...&color=%231D9E75&footer=...
export default function Preview() {
    const [p] = useSearchParams();
    // Banner mirrors AppContext.apiToBrand: fall back one to the other so a single image still shows.
    const bl = p.get('bannerLarge');
    const bs = p.get('bannerSmall');
    const large = bl || bs || null;
    const small = bs || bl || null;
    const brand = {
        name: p.get('brandName') || 'Your Brand',
        // No default logo: each template applies its own live fallback (Modern/Minimal show the
        // brand name, Classic shows its logo-bar fallback) — the preview must match the live page.
        logo: p.get('logo') || null,
        webUrl: p.get('webUrl') || null,
        bannerBgColor: p.get('color') || null,
        footer: p.get('footer') || null,
        banner: large && small ? { large, small } : undefined,
        instaUrl: p.get('instaUrl') || null,
        fbUrl: p.get('fbUrl') || null,
        twitterUrl: p.get('twitterUrl') || null,
        linkedinUrl: p.get('linkedinUrl') || null,
        whatsappNumber: p.get('whatsappNumber') || null,
        termsAndConditions: p.get('termsAndConditions') || null,
        returnPolicy: p.get('returnPolicy') || null,
        privacyPolicy: p.get('privacyPolicy') || null,
    };
    // Same theme derivation as the live page (ThemedApp): the brand's single accent colour also
    // tints the Classic theme — so the preview shows exactly what going live will look like.
    return (
        <ThemeProvider theme={buildTheme(deriveColorsFromAccent(p.get('color')))}>
            <TemplateRenderer
                template={p.get('template') || 'CLASSIC'}
                brand={brand}
                order={SAMPLE_ORDER}
                preview
            />
        </ThemeProvider>
    );
}
